<template>
  <card card-body-classes="table-full-width" no-footer-line>
    <h4 slot="header" class="card-title">
      {{ $t("message.recurringInvoices") }}
    </h4>
    <div class="card-body">
      <b-form class="d-flex mb-2" inline @submit.prevent>
        <b-form-select
          v-model="pagination.perPage"
          aria-controls="recurringInvoices__table"
          :options="pagination.perPageOptions"
        >
        </b-form-select>
        <fg-input
          id="recurringInvoices__searchInput"
          class="ml-2"
          v-model="searchQuery"
          placeholder="Search..."
          aria-label="Search"
          aria-controls="recurringInvoices__table"
        ></fg-input>
        <router-link
          class="ml-auto mt-0 btn btn-primary"
          to="/recurring-invoices/add"
        >
          {{ $t("message.add") }}
        </router-link>
      </b-form>
      <b-table
        id="recurringInvoices__table"
        :items="recurringInvoices"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter="searchQuery"
        :per-page="pagination.perPage"
        :current-page="pagination.currentPage"
        :busy="isLoading"
        :show-empty="!isLoading && !recurringInvoices.length"
        stacked="md"
        striped
        @filtered="filteredItems => (searchItemsLength = filteredItems.length)"
      >
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template v-slot:empty>
          <div v-if="error" class="text-center text-danger my-2">
            <p>{{ $t("message.somethingWentWrong") }}</p>
            <p>{{ error }}</p>
          </div>
          <div v-else class="text-center text-primary my-2">
            <p>{{ $t("message.noRecurringInvoices") }}</p>
          </div>
        </template>
        <template v-slot:cell(to)="data">
          <router-link tag="a" :to="`/customers/view/${data.item.to._id}`">{{
            data.item.to.companyName
          }}</router-link>
        </template>
        <template v-slot:cell(status)="data">
          <span
            :class="`${formatRecurringInvoiceStatusBadgeClass(data.item)}`"
            >{{ formatRecurringInvoiceStatus(data.item) }}</span
          >
        </template>
        <template v-slot:cell(actions)="data">
          <drop-down
            class="row-actions"
            tag="button"
            position="right"
            icon="fa fa-ellipsis-v"
            hideArrow
            v-if="$can('update', 'RECURRING_INVOICES')"
          >
            <router-link
              tag="a"
              class="dropdown-item"
              :to="`/recurring-invoices/view/${data.item._id}`"
              >{{ $t("message.view") }}</router-link
            >
            <button
              v-if="recurringInvoiceActive(data.item)"
              class="dropdown-item text-danger"
              @click="handleTerminateRecurringInvoice(data.item)"
            >
              {{ $t("message.cancelRecurringInvoice") }}
            </button>
          </drop-down>
        </template>
      </b-table>
      <div class="d-flex justify-content-between">
        <div>
          <span class="card-category"
            >Showing {{ from + 1 }} to {{ to }} of {{ total }} entries</span
          >
        </div>
        <n-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        />
      </div>
    </div>
  </card>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import { Pagination as NPagination } from "src/components";
import _ from "lodash";
import { dateToHuman } from "@/helpers/helpers";

export default {
  components: {
    NPagination
  },
  data() {
    return {
      loader: null,
      fields: [
        {
          key: "to",
          label: this.$t("message.recurringInvoiceBillingTo"),
          sortable: true
        },
        {
          key: "period",
          label: this.$t("message.recurringInvoicePeriod"),
          formatter: this.formatRecurringInvoicePeriod,
          sortable: true
        },
        {
          key: "total",
          label: this.$t("message.invoiceValue"),
          formatter: this.formatRecurringInvoiceTotal,
          sortable: true
        },
        {
          key: "dueDate",
          label: this.$t("message.recurringInvoiceNextIssue"),
          formatter: dateToHuman,
          sortable: true
        },
        {
          key: "ending",
          label: this.$t("message.recurringInvoiceEnding"),
          formatter: this.formatRecurringInvoiceEnding,
          sortable: true
        },
        {
          key: "status",
          label: this.$t("message.recurringInvoiceStatus"),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("message.actions"),
          sortable: false
        }
      ],
      searchQuery: null,
      searchItemsLength: 0,
      sortBy: "to",
      sortDesc: false,
      pagination: {
        perPage: 5,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50]
      },
      isLoading: false,
      error: null
    };
  },
  methods: {
    ...mapActions(["fetchRecurringInvoices", "terminateRecurringInvoice"]),
    formatRecurringInvoicePeriod(value, key, item) {
      if (item.recurringConfig.daily) {
        return this.$t("message.recurringInvoiceDaily");
      } else if (item.recurringConfig.weekly) {
        return this.$t("message.recurringInvoiceWeekly");
      } else if (item.recurringConfig.monthly) {
        return this.$t("message.recurringInvoiceMonthly");
      }
      return this.$t("message.recurringInvoiceYearly");
    },
    formatRecurringInvoiceTotal(value) {
      return value ? value.displayValue : "";
    },
    formatRecurringInvoiceEnding(value, key, item) {
      if (!item.status === "ACTIVE") {
        return "Ended";
      }

      const { endDate } = item;

      if (endDate) {
        return `On ${dateToHuman(endDate.seconds)}`;
      }

      const { totalOccurrences, issuedCount } = item;

      if (totalOccurrences) {
        return `${this.$t("message.recurringInvoiceAfter")} ${totalOccurrences -
          (issuedCount ? issuedCount : 0)} ${_.toLower(
          this.$t("message.recurringInvoiceMore")
        )} ${_.toLower(this.$t("message.recurringInvoiceOccurrences"))}`;
      }
    },
    formatRecurringInvoiceStatusBadgeClass(invoice) {
      const { status } = invoice;

      if (status === "COMPLETE") {
        return "badge badge-success";
      }

      if (status === "CANCELLED") {
        return "badge badge-warning";
      }

      return "badge badge-info";
    },
    formatRecurringInvoiceStatus(invoice) {
      const { status } = invoice;

      if (status === "COMPLETE") {
        return this.$t("message.recurringInvoiceStatusComplete");
      }

      if (status === "CANCELLED") {
        return this.$t("message.recurringInvoiceStatusCancelled");
      }

      return this.$t("message.recurringInvoiceStatusActive");
    },
    recurringInvoiceActive(invoice) {
      return invoice.status === "ACTIVE";
    },
    async handleTerminateRecurringInvoice(recurringInvoice) {
      const confirmationResult = this.$swal({
        title: this.$t("message.areYouSure"),
        text: this.$t("message.cancelRecurringInvoiceMessage"),
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-success btn-fill",
        cancelButtonClass: "btn btn-danger btn-fill",
        buttonsStyling: false
      });

      if (!confirmationResult.value) return;

      this.loader = this.$loading.show();

      try {
        await this.terminateRecurringInvoice(recurringInvoice);
        this.$notify({
          type: "success",
          title: this.$t("message.success"),
          text: this.$t("message.cancelRecurringInvoiceSuccess")
        });
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text:
            this.$t("message.cancelRecurringInvoiceFailure") +
            `${error.message}.`
        });
      } finally {
        this.loader.hide();
      }
    },
    showLoadingIndicator() {
      this.isLoading = true;
    },
    hideLoadingIndicator() {
      this.isLoading = false;
    },
    showError(error) {
      this.error = error;
    }
  },
  computed: {
    ...mapGetters(["recurringInvoices"]),
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchQuery
        ? this.searchItemsLength
        : this.recurringInvoices.length;
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    }
  },
  async created() {
    try {
      this.showLoadingIndicator();
      await this.fetchRecurringInvoices();
    } catch (e) {
      this.showError(e.message);
    } finally {
      this.hideLoadingIndicator();
    }
  }
};
</script>
